<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentFunnel: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const store = useStore();

// Reactive filter state
const filters = ref({
  priorities: [],
  valueMin: null,
  valueMax: null,
  agent: '',
  dateStart: '',
  dateEnd: '',
  scheduledDateStart: '',
  scheduledDateEnd: '',
});

// Priority options
const priorities = computed(() => [
  { value: 'urgent', label: t('KANBAN.PRIORITY_LABELS.URGENT') },
  { value: 'high', label: t('KANBAN.PRIORITY_LABELS.HIGH') },
  { value: 'medium', label: t('KANBAN.PRIORITY_LABELS.MEDIUM') },
  { value: 'low', label: t('KANBAN.PRIORITY_LABELS.LOW') },
]);

// Search query for agents
const agentSearchQuery = ref('');

// Filtered agents computed
const agents = computed(() => {
  const agentsFromStore = store.getters['agents/getAgents'] || [];
  if (!agentSearchQuery.value) return agentsFromStore;

  const query = agentSearchQuery.value.toLowerCase();
  return agentsFromStore.filter(
    agent =>
      agent &&
      agent.name &&
      agent.email &&
      (agent.name.toLowerCase().includes(query) ||
        agent.email.toLowerCase().includes(query))
  );
});

const hasActiveFilters = computed(() => {
  return (
    (filters.value.priorities && filters.value.priorities.length > 0) ||
    filters.value.valueMin !== null ||
    filters.value.valueMax !== null ||
    filters.value.agent !== '' ||
    filters.value.dateStart !== '' ||
    filters.value.dateEnd !== '' ||
    filters.value.scheduledDateStart !== '' ||
    filters.value.scheduledDateEnd !== ''
  );
});

const togglePriority = priority => {
  const currentPriorities = filters.value.priorities || [];
  const index = currentPriorities.indexOf(priority);
  if (index > -1) {
    currentPriorities.splice(index, 1);
  } else {
    currentPriorities.push(priority);
  }
  filters.value.priorities = [...currentPriorities];
};

const clearFilters = () => {
  filters.value = {
    priorities: [],
    valueMin: null,
    valueMax: null,
    agent: '',
    dateStart: '',
    dateEnd: '',
    scheduledDateStart: '',
    scheduledDateEnd: '',
  };
  agentSearchQuery.value = '';
};

const handleExport = () => {
  // Clean up empty values
  const cleanFilters = {
    priorities: filters.value.priorities || [],
    valueMin: filters.value.valueMin || null,
    valueMax: filters.value.valueMax || null,
    agent: filters.value.agent || '',
    dateStart: filters.value.dateStart || '',
    dateEnd: filters.value.dateEnd || '',
    scheduledDateStart: filters.value.scheduledDateStart || '',
    scheduledDateEnd: filters.value.scheduledDateEnd || '',
  };

  emit('confirm', cleanFilters);
};

// Watch for modal close to reset form
watch(
  () => props.show,
  isShowing => {
    if (!isShowing) {
      clearFilters();
    }
  }
);

// Load agents on mount
onMounted(async () => {
  try {
    await store.dispatch('agents/get');
  } catch (error) {
    // Error handling
  }
});
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="emit('close')">
      <div class="modal-container">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-extrabold">Exportar Itens</h3>
            <button
              type="button"
              class="p-1 hover:bg-n-alpha-2 rounded transition-colors"
              @click="emit('close')"
            >
              <span class="i-ri-close-line size-5" />
            </button>
          </div>

          <div class="space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            <p class="text-sm text-n-slate-11">
              Os itens do funil selecionado serão exportados em formato CSV.
              Aplique filtros opcionais para exportar apenas os itens desejados.
            </p>

            <!-- Priority -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                {{ t('KANBAN.FILTER.PRIORITY_LABEL') }}
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="priority in priorities"
                  :key="priority.value"
                  class="px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 transform hover:scale-105 active:scale-95"
                  :class="[
                    filters.priorities &&
                    filters.priorities.includes &&
                    filters.priorities.includes(priority.value)
                      ? 'shadow-lg'
                      : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-2',
                  ]"
                  :style="{
                    backgroundColor:
                      filters.priorities &&
                      filters.priorities.includes &&
                      filters.priorities.includes(priority.value)
                        ? priority.value === 'urgent'
                          ? '#FFDCE1'
                          : priority.value === 'high'
                            ? '#DBEAFE'
                            : priority.value === 'medium'
                              ? '#FEF3C7'
                              : '#D1FAE5'
                        : '',
                    color:
                      filters.priorities &&
                      filters.priorities.includes &&
                      filters.priorities.includes(priority.value)
                        ? priority.value === 'urgent'
                          ? '#DC3B5D'
                          : priority.value === 'high'
                            ? '#1E40AF'
                            : priority.value === 'medium'
                              ? '#92400E'
                              : '#065F46'
                        : '',
                  }"
                  @click="togglePriority(priority.value)"
                >
                  {{ priority.label }}
                </button>
              </div>
            </div>

            <!-- Value -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                {{ t('KANBAN.FILTER.VALUE_LABEL') }}
              </label>
              <div class="flex gap-3">
                <div class="flex-1">
                  <input
                    v-model.number="filters.valueMin"
                    type="number"
                    :placeholder="t('KANBAN.FILTER.VALUE.MIN_PLACEHOLDER')"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white placeholder-n-slate-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <div class="flex-1">
                  <input
                    v-model.number="filters.valueMax"
                    type="number"
                    :placeholder="t('KANBAN.FILTER.VALUE.MAX_PLACEHOLDER')"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white placeholder-n-slate-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            <!-- Agent -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                {{ t('KANBAN.FILTER.AGENT.LABEL') }}
              </label>
              <div class="relative">
                <input
                  v-model="agentSearchQuery"
                  type="text"
                  :placeholder="t('KANBAN.FILTER.AGENT.SEARCH_PLACEHOLDER')"
                  class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white placeholder-n-slate-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-10"
                />
                <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    class="w-4 h-4 text-n-slate-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              <div class="mt-3 max-h-48 overflow-y-auto">
                <div
                  v-for="agent in agents"
                  :key="agent.id"
                  class="group relative overflow-hidden rounded-2xl border transition-all duration-200 transform active:scale-[0.99] cursor-pointer mb-2"
                  :class="[
                    filters.agent === agent.id
                      ? 'border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 ring-1 ring-blue-300/50 dark:ring-blue-500/20'
                      : 'border-n-weak bg-n-solid-2',
                  ]"
                  @click="
                    filters.agent = filters.agent === agent.id ? '' : agent.id
                  "
                >
                  <div class="relative flex items-center gap-4 p-4">
                    <div class="flex-shrink-0 relative">
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm transition-all duration-200"
                        :style="{
                          background:
                            'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                        }"
                      >
                        {{
                          agent.name ? agent.name.charAt(0).toUpperCase() : '?'
                        }}
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <div
                          class="font-semibold text-sm transition-colors duration-200 truncate"
                          :class="[
                            filters.agent === agent.id
                              ? 'text-blue-900 dark:text-blue-100'
                              : 'text-n-slate-12',
                          ]"
                        >
                          {{ agent.name }}
                        </div>
                      </div>
                      <div
                        class="text-xs transition-colors duration-200 truncate"
                        :class="[
                          filters.agent === agent.id
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-n-slate-11',
                        ]"
                      >
                        {{ agent.email }}
                      </div>
                    </div>
                    <div
                      class="flex-shrink-0 transition-all duration-200"
                      :class="[
                        filters.agent === agent.id
                          ? 'opacity-100 scale-100'
                          : 'opacity-0 scale-75',
                      ]"
                    >
                      <div
                        class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shadow-md"
                      >
                        <svg
                          class="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="agents.length === 0"
                  class="text-center text-n-slate-11 py-6 text-sm"
                >
                  {{
                    agentSearchQuery
                      ? t('KANBAN.FILTER.AGENT.NO_AGENT_FOUND')
                      : t('KANBAN.FILTER.AGENT.LOADING_AGENTS')
                  }}
                </div>
              </div>
            </div>

            <!-- Date -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                {{ t('KANBAN.FILTER.CREATION_PERIOD') }}
              </label>
              <div class="flex gap-3">
                <div class="flex-1">
                  <input
                    v-model="filters.dateStart"
                    type="date"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <div class="flex-1">
                  <input
                    v-model="filters.dateEnd"
                    type="date"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            <!-- Scheduled Date -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                {{ t('KANBAN.FILTER.SCHEDULING_PERIOD') }}
              </label>
              <div class="flex gap-3">
                <div class="flex-1">
                  <input
                    v-model="filters.scheduledDateStart"
                    type="date"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <div class="flex-1">
                  <input
                    v-model="filters.scheduledDateEnd"
                    type="date"
                    class="w-full px-4 py-3 border border-n-weak rounded-2xl bg-n-solid-3 text-n-slate-12 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="border-t border-n-weak mt-6 pt-4">
            <div class="flex items-center justify-between">
              <button
                :disabled="!hasActiveFilters"
                class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors duration-200"
                :class="
                  hasActiveFilters
                    ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-n-slate-10 cursor-not-allowed'
                "
                @click="clearFilters"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                {{ t('KANBAN.FILTER.CLEAR') }}
              </button>

              <div class="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="emit('close')"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  color="blue"
                  size="sm"
                  @click="handleExport"
                >
                  Exportar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal-overlay {
  @apply fixed inset-0 z-[9999] flex items-center justify-center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-container {
  @apply bg-n-solid-2 rounded-xl shadow-xl;
  @apply max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden;
  animation: modalFadeIn 0.2s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
